/**
 * 紙の描き手（#217、版 2）＝見積書・請求書の紙はプリセットも自分の書式もこの1本。API の PDF もエディタのプレビューも同じ関数。
 *   ・部品は書式の位置（mm）に置く。同じ「詰める組」（stack）の部品は印刷のとき 空の項目・無い画像を詰め、何行にもなる項目の分だけ下を押す
 *     （発行者欄・頭の欄・宛名が今の紙と同じに見えるように）。組が伸び縮みした分だけ明細表も上下する
 *   ・明細表：行の高さは中身に合わせる（wrap。名称・規格・単位・備考は折り返す）／数字の欄は1行に収める（入らなければ文字を小さく＝桁を切らない）
 *   ・合計を表の中に（foot：小計 → 消費税（税率ごと） → 立替金 → 合計（税込））、明細表の下の枠（boxes：訂正の内容・別途・注記・備考）
 *   ・ページの下まで空行で埋める（fill。既定 ON）：並びは 明細 → 空行 → 合計。最初の空行の名称の欄に「以下余白」。
 *     枠（備考など）があるページは 枠の上まで埋める。枠が入らなければ空行を減らし、それでも入らなければ枠は次のページへ（行は失わない）
 *   ・表紙＋内訳の紙（sections）：表紙のあとは必ず改ページ。内訳は続けて刷る（見出し・表の見出し・最初の2行が入らなければ次のページ）か、
 *     内訳ごとに改ページ（会社の設定）。改ページのときは内訳ごとの最後のページも空行で埋める。表紙の「別紙 (P. n)」はここで決まったページ
 *     書式に内訳の紙の設定（table.sections）が無くても（#217 より前のカスタム書式）、データに内訳の紙があれば描く（見出しは標準の見た目＝DEFAULT_SECTIONS）
 *   ・2ページ目から：repeat の部品（番号・日付＝右上・ページの下の帳票名・ページ番号）と表。表の見出しはページごとに繰り返す。
 *     表より上に出す部品は上の余白まで寄せ、表と内訳の見出しはその下から（2026-10-19：紙がばらけても どの書類のどの日のものか分かるように）
 *   ・文字の折り返しは text.ts（自分で折って <br> でつなぐ）＝どの環境でも同じ行数・同じページ割り
 *   ・mode=edit：1ページ目だけ、詰めない（書式の位置のまま）、表は入るだけの行＋合計＋枠、部品に data-id、印字しない部品も薄く
 */
import type { Labels, PaperBox, PaperCol, PaperData, PaperEl, PaperFootRow, PaperLayout, PaperRow, PaperRule, PaperSection, PaperTable, PaperTableSections } from "@genbaryoku/contract";
import { esc } from "./fmt.js";
import { qrContent, qrSvg } from "./qr.js";
import { PT, fitSize, textWidthMm, wrapText } from "./text.js";

export type RenderMode = "print" | "edit";
export type Box = { x: number; y: number; w: number; h: number };
export type Font = "serif" | "sans" | "maru";

/** 書体（style 属性の中に書くので、書体の名前は単引用符） */
export const FONT_CSS: Record<Font, string> = {
  serif: `'Noto Serif CJK JP','Noto Serif JP','Hiragino Mincho ProN','Yu Mincho','BIZ UDMincho',serif`,
  sans: `'Noto Sans CJK JP','Noto Sans JP','Hiragino Sans','Yu Gothic','BIZ UDPGothic',sans-serif`,
  maru: `'Zen Maru Gothic','Kosugi Maru','Hiragino Maru Gothic ProN','Noto Sans CJK JP','Noto Sans JP',sans-serif`,
};

export type Render2Options = {
  mode?: RenderMode;
  assets?: Record<string, string>;
  labels?: Labels;
  /** この紙の後ろにつなぐページの数（変更点・積算表）。ページ番号の「n / N」の N に足す */
  extraPages?: number;
  /** 空行を入れない（#217 の前の紙との見くらべ用。apps/api/test/tools/paper-compare.ts だけが使う） */
  noBlank?: boolean;
};

/** 1ページ目の部品と表の位置（エディタの選択・枠・当たり判定・注意の計算に使う） */
export type Geometry = {
  els: Record<string, Box>;
  /** 明細表の全体の枠（見出し＋行＋空行＋合計） */
  table: Box;
  /** 列の境（紙の上の x） */
  cols: Array<{ id: string; label: string; x: number; w: number }>;
  /** dock の部品の基準（表の下端＋3mm） */
  dockBase: number;
  /** 備考の下に付ける部品（dock end、#233 2d）の基準（最後の枠の下端＋3mm。1ページ目に無ければ dockBase） */
  endBase?: number;
  /** 明細表の下の枠（備考など） */
  boxes: Box[];
  /** 欄に入りきらない文字（見本のデータで。注意の「はみ出し」） */
  overflow: Array<{ target: string; text: string }>;
};

const r2 = (v: number): number => Math.round(v * 100) / 100;
const mm = (v: number): string => `${r2(v)}mm`;
const pt = (v: number): string => `${v}pt`;
const DEFAULT_LH = 1.35;
const lineMm = (size: number, lh: number): number => size * lh * PT;

/* ---------------------------------------------------------------- 線 */

/**
 * 二重線の最小の幅 0.8mm（約 3.02px）。Chromium は線の幅を整数の px に切り下げ、3px 未満の double は1本の線で描く
 * （0.79mm＝2.99px は 2px になり、本番の PDF で題字の下・自社の左の二重線が1本に見えた。#217 の本番の確かめ）。前の紙は 3px double
 */
const DBL_MIN = 0.8;
const ruleW = (r: PaperRule): number => (r.style === "double" ? Math.max(r.w, DBL_MIN) : r.w);
const ruleOn = (r: PaperRule | undefined | null): r is PaperRule => !!r && r.style !== "none" && r.w > 0;
function hline(x: number, y: number, w: number, r: PaperRule): string {
  const lw = ruleW(r);
  return `<div style="position:absolute;left:${mm(x)};top:${mm(y - lw / 2)};width:${mm(w)};height:0;border-top:${mm(lw)} ${r.style} ${r.color};"></div>`;
}
function vline(x: number, y: number, h: number, r: PaperRule): string {
  const lw = ruleW(r);
  return `<div style="position:absolute;left:${mm(x - lw / 2)};top:${mm(y)};width:0;height:${mm(h)};border-left:${mm(lw)} ${r.style} ${r.color};"></div>`;
}
function frameBox(x: number, y: number, w: number, h: number, r: PaperRule): string {
  const lw = ruleW(r);
  return `<div style="position:absolute;left:${mm(x - lw / 2)};top:${mm(y - lw / 2)};width:${mm(w + lw)};height:${mm(h + lw)};border:${mm(lw)} ${r.style} ${r.color};box-sizing:border-box;"></div>`;
}

/* ---------------------------------------------------------------- 明細表の行 */

type Align = "left" | "center" | "right";
type Cell = {
  x: number;
  w: number;
  lines: string[];
  size: number;
  bold: boolean;
  align: Align;
  color: string;
  font?: Font;
  /** 「（対象 ○円）」（合計の行の名前の後ろ） */
  sub?: { text: string; size: number; color: string };
  /** 「別紙 (P. n)」の内訳の紙の id */
  ref?: string;
  /** 右の余白を広く（合計の行の名前） */
  padR?: number;
  /** 左の余白（#302：No〜を結合した見出し・文章の行は文字を名称の列から始める） */
  padL?: number;
  /** 1行に収めた数字（文字を小さくしてもなお書体で入りきらないとき、切らずに見せる） */
  fit?: boolean;
};
type RowBox = {
  kind: "row" | "blank" | "foot";
  h: number;
  cells: Cell[];
  fill: string;
  /** 行の上の線（null＝見出し行のすぐ下＝見出しの線を使う） */
  top: PaperRule | null;
  /** 最初の空行（「以下余白」） */
  rest?: boolean;
  /** 入りきらない文字（注意） */
  overflow?: string[];
};

const WRAP_COLS = new Set(["name", "spec", "unit", "notes"]);
const FIT_COLS = new Set(["no", "qty", "price", "amount"]);

/** 明細表の決まった値（書式に無ければ前の見た目） */
function tableSpec(t: PaperTable) {
  const lh = t.lh ?? 1.3;
  const padX = t.padX ?? 1;
  const cols = t.cols.filter((c) => c.on).sort((a, b) => a.x - b.x);
  const padV = Math.max(0, t.pitch - lineMm(t.size, lh));
  return { lh, padX, cols, padV, headH: t.header.on ? t.header.h : 0, wrap: t.wrap === true, look: t.look };
}
type Spec = ReturnType<typeof tableSpec>;

/** 1つの欄の高さ（文字の大きさと行の数から） */
const cellH = (s: Spec, size: number, n: number): number => s.padV + Math.max(1, n) * lineMm(size, s.lh);

function makeCell(s: Spec, x: number, w: number, text: string, o: { size: number; bold?: boolean; align: Align; color: string; wrap: boolean; fit: boolean; font?: Font }): { cell: Cell; overflow: boolean } {
  const inner = Math.max(0.5, w - 2 * s.padX);
  let size = o.size;
  let lines: string[];
  let overflow = false;
  if (o.fit) {
    // 太字の数字は細字より広い（6% 広めに見積もる）
    size = fitSize(text, o.bold ? inner / 1.06 : inner, o.size);
    lines = [text];
    overflow = size < o.size * 0.8;
  } else if (o.wrap) {
    lines = wrapText(text, inner, o.size);
  } else {
    lines = [text.replace(/\r?\n/g, " ")];
    overflow = textWidthMm(lines[0]!, o.size) > inner + 0.2;
  }
  return { cell: { x, w, lines, size, bold: !!o.bold, align: o.align, color: o.color, ...(o.font ? { font: o.font } : {}), ...(o.fit ? { fit: true } : {}) }, overflow };
}

/** 欄の範囲（見える列の番号 a〜b）→ 紙の x と幅（表の左端から） */
const span = (cols: PaperCol[], a: number, b: number): [number, number] => {
  const ca = cols[a]!;
  const cb = cols[b]!;
  return [ca.x, cb.x + cb.w - ca.x];
};

function rowOf(t: PaperTable, s: Spec, r: PaperRow, pageRefLabel: (ref: string) => string, zebra: boolean): RowBox {
  const cols = s.cols;
  const L = s.look;
  const iName = Math.max(0, cols.findIndex((c) => c.id === "name"));
  const iAmt = cols.findIndex((c) => c.id === "amount");
  const last = cols.length - 1;
  const noColor = L?.noColor || t.color;
  const notesColor = L?.notesColor || t.color;
  const cells: Cell[] = [];
  const over: string[] = [];
  const push = (m: { cell: Cell; overflow: boolean }, what: string) => {
    cells.push(m.cell);
    if (m.overflow) over.push(what);
  };
  // はみ出しの注意に出す列の名前は書式の列の見出し（#222：名称・規格・備考は会社の言葉）
  const nameWhat = cols[iName]?.label || "名称";
  const notesWhat = cols[last]?.label || "備考";
  // No. の欄（名称より左の列）を1つの欄に
  const lead = () => {
    if (iName > 0) {
      const [x, w] = span(cols, 0, iName - 1);
      cells.push({ x, w, lines: [""], size: t.size, bold: false, align: "left", color: noColor });
    }
  };
  let fill = "";
  let top: PaperRule | null = t.rules.h;
  /** #302（C2）：名称の列の頭まで（No の列の幅）。結合した欄の文字はここから */
  const indent = cols[iName] ? cols[iName]!.x - cols[0]!.x : 0;
  switch (r.kind) {
    case "heading":
    case "sheet": {
      if (r.kind === "heading" && t.spanRows) {
        // 見出し行：No〜備考を1つの枠（文字は名称の列から）。折り返しの幅は名称の列から右端まで
        const [x0, w0] = span(cols, 0, last);
        const m = makeCell(s, x0 + indent, w0 - indent, r.name, { size: t.size, bold: true, align: "left", color: t.color, wrap: s.wrap, fit: false });
        m.cell.x = x0;
        m.cell.w = w0;
        m.cell.padL = indent + s.padX;
        push(m, nameWhat);
        fill = L?.groupFill ?? "";
        break;
      }
      lead();
      const toNotes = r.kind === "sheet" && cols[last]?.id === "notes" && last > iName;
      const [x, w] = span(cols, iName, toNotes ? last - 1 : last);
      push(makeCell(s, x, w, r.name, { size: t.size, bold: true, align: "left", color: t.color, wrap: s.wrap, fit: false }), nameWhat);
      if (toNotes) {
        const c = cols[last]!;
        push(makeCell(s, c.x, c.w, r.notes, { size: c.size, align: c.align, color: notesColor, wrap: s.wrap, fit: false }), notesWhat);
      }
      fill = (r.kind === "sheet" ? L?.secFill : L?.groupFill) ?? "";
      if (r.kind === "sheet" && t.foot && ruleOn(t.foot.rule)) top = t.foot.rule;
      break;
    }
    case "text": {
      const size = L?.textSize ?? Math.max(6, (cols[iName]?.size ?? t.size) - 1);
      if (t.spanRows) {
        // 文章（説明）の行：No〜名称を1つの枠（文字は名称の列から・名称の幅で折り返す）。規格から右は空の欄＝枠を残す
        const [x0, w0] = span(cols, 0, iName);
        const m = makeCell(s, x0 + indent, w0 - indent, r.name, { size, align: "left", color: L?.textColor || t.color, wrap: true, fit: false });
        m.cell.x = x0;
        m.cell.w = w0;
        m.cell.padL = indent + s.padX;
        push(m, nameWhat);
        for (let k = iName + 1; k <= last; k++) cells.push({ x: cols[k]!.x, w: cols[k]!.w, lines: [""], size: t.size, bold: false, align: "left", color: t.color });
        break;
      }
      lead();
      const [x, w] = span(cols, iName, last);
      push(makeCell(s, x, w, r.name, { size, align: "left", color: L?.textColor || t.color, wrap: s.wrap, fit: false }), nameWhat);
      break;
    }
    case "subtotal":
    case "sheetTotal": {
      lead();
      if (iAmt > iName) {
        const [x, w] = span(cols, iName, iAmt - 1);
        const m = makeCell(s, x, w, r.name, { size: cols[iName]?.size ?? t.size, bold: r.kind === "sheetTotal" && !t.look, align: "right", color: t.color, wrap: false, fit: false });
        m.cell.padR = s.padX + 2;
        push(m, nameWhat);
        const a = cols[iAmt]!;
        push(makeCell(s, a.x, a.w, r.amount, { size: a.size, bold: r.kind === "sheetTotal" && !t.look, align: a.align, color: t.color, wrap: false, fit: true }), "金額");
        if (iAmt < last) {
          const [x2, w2] = span(cols, iAmt + 1, last);
          cells.push({ x: x2, w: w2, lines: [""], size: t.size, bold: false, align: "left", color: t.color });
        }
      } else {
        const [x, w] = span(cols, iName, last);
        push(makeCell(s, x, w, `${r.name}　${r.amount}`, { size: t.size, align: "right", color: t.color, wrap: false, fit: false }), nameWhat);
      }
      fill = L?.subFill ?? "";
      break;
    }
    default: {
      for (const c of cols) {
        let text = c.id === "no" ? r.no : (r[c.id] ?? "");
        if (c.id === "spec" && r.ref) text = `${text}${pageRefLabel(r.ref)}`;
        const color = c.id === "no" ? noColor : c.id === "notes" ? notesColor : t.color;
        const m = makeCell(s, c.x, c.w, text, { size: c.size, align: c.align, color, wrap: s.wrap && WRAP_COLS.has(c.id), fit: FIT_COLS.has(c.id) });
        if (c.id === "spec" && r.ref) m.cell.ref = r.ref;
        push(m, c.label || c.id);
      }
      if (zebra && t.zebra) fill = t.zebra;
    }
  }
  const h = Math.max(t.pitch, ...cells.map((c) => cellH(s, c.size, c.lines.length)));
  return { kind: "row", h, cells, fill, top, ...(over.length ? { overflow: over } : {}) };
}

/** 合計の行（小計・消費税・立替金・合計（税込））と内訳の合計 */
function footOf(t: PaperTable, s: Spec, f: PaperFootRow, first: boolean): RowBox {
  const ft = t.foot!;
  const cols = s.cols;
  const iAmt = cols.findIndex((c) => c.id === "amount");
  const last = cols.length - 1;
  const total = f.kind === "total";
  const size = total ? ft.totalSize : ft.size;
  const cells: Cell[] = [];
  if (iAmt > 0) {
    const [x, w] = span(cols, 0, iAmt - 1);
    cells.push({ x, w, lines: [f.label], size, bold: total, align: "right", color: t.color, padR: s.padX + 2, ...(f.sub ? { sub: { text: f.sub, size: ft.subSize, color: ft.subColor } } : {}) });
    const a = cols[iAmt]!;
    cells.push(makeCell(s, a.x, a.w, f.amount, { size: total ? ft.totalSize : Math.max(size, a.size), bold: total, align: "right", color: t.color, wrap: false, fit: true }).cell);
    if (iAmt < last) {
      const [x2, w2] = span(cols, iAmt + 1, last);
      cells.push({ x: x2, w: w2, lines: [""], size, bold: false, align: "left", color: t.color });
    }
  } else {
    const [x, w] = span(cols, 0, last);
    cells.push({ x, w, lines: [`${f.label}　${f.amount}`], size, bold: total, align: "right", color: t.color });
  }
  const h = Math.max(t.pitch, ...cells.map((c) => cellH(s, c.size, 1)));
  return { kind: "foot", h, cells, fill: total ? ft.totalFill : "", top: first || total ? ft.rule : t.rules.h };
}

function blankOf(t: PaperTable, s: Spec, h: number, rest: boolean): RowBox {
  const cells: Cell[] = s.cols.map((c) => ({ x: c.x, w: c.w, lines: [""], size: c.size, bold: false, align: "left" as Align, color: t.color }));
  return { kind: "blank", h, cells, fill: "", top: t.rules.h, ...(rest ? { rest: true } : {}) };
}

/* ---------------------------------------------------------------- 明細表を描く */

type TablePart = { top: number; rows: RowBox[]; header: boolean; taxNote?: string };

function cellHtml(s: Spec, c: Cell, y: number, rowH: number, mode: RenderMode): string {
  const lh = lineMm(c.size, s.lh);
  const padTop = s.padV / 2;
  const text = c.lines.map((l) => esc(l)).join("<br>");
  const sub = c.sub ? `<span style="font-size:${pt(c.sub.size)};font-weight:400;color:${c.sub.color};margin-left:1em">${esc(c.sub.text)}</span>` : "";
  const body = c.ref && mode === "print" ? `<a href="#${esc(c.ref)}" style="color:inherit;text-decoration:none">${text}</a>` : text;
  const padR = c.padR ?? s.padX;
  const padL = c.padL ?? s.padX;
  return `<div style="position:absolute;left:${mm(c.x)};top:${mm(y)};width:${mm(c.w)};height:${mm(rowH)};overflow:${c.fit ? "visible" : "hidden"};box-sizing:border-box;padding:${mm(padTop)} ${mm(padR)} 0 ${mm(padL)};font-family:${FONT_CSS[c.font ?? "sans"]};font-size:${pt(c.size)};font-weight:${c.bold ? 700 : 400};color:${c.color || "#1A1A18"};line-height:${mm(lh)};text-align:${c.align};white-space:pre;">${body}${sub}</div>`;
}

function tableHtml(t: PaperTable, s: Spec, x0: number, part: TablePart, mode: RenderMode, pre: boolean, restText: string): { html: string; h: number } {
  const out: string[] = [];
  const headH = part.header ? s.headH : 0;
  let H = headH;
  for (const r of part.rows) H += r.h;
  const attrs = mode === "edit" ? ` data-id="table" data-type="table"` : "";
  out.push(`<div class="el tbl"${attrs} style="position:absolute;left:${mm(x0)};top:${mm(part.top)};width:${mm(t.w)};height:${mm(H)};">`);
  // 地色
  if (!pre) {
    if (headH && t.header.fill) out.push(`<div style="position:absolute;left:0;top:0;width:100%;height:${mm(headH)};background:${t.header.fill};"></div>`);
    let y = headH;
    for (const r of part.rows) {
      if (r.fill) out.push(`<div style="position:absolute;left:0;top:${mm(y)};width:100%;height:${mm(r.h)};background:${r.fill};"></div>`);
      y += r.h;
    }
  }
  // 見出し
  if (headH && !pre) {
    for (const c of s.cols) {
      // 見出しが列に入らないときだけ文字を小さくする（#222：会社の言葉の見出しは6文字まで。列の幅は変えない＝切れないように。入る見出しは今までと同じ大きさ）
      const hs = fitSize(c.label, c.w - 2 * s.padX, t.header.size, 5);
      out.push(cellHtml({ ...s, padV: Math.max(0, headH - lineMm(hs, s.lh)) }, { x: c.x, w: c.w, lines: [c.label], size: hs, bold: t.header.bold, align: c.labelAlign, color: t.header.color }, 0, headH, mode));
    }
  }
  // 行の中身
  let y = headH;
  for (const r of part.rows) {
    for (const c of r.cells) if (c.lines.some((l) => l !== "") || c.sub) out.push(cellHtml(s, c, y, r.h, mode));
    if (r.rest && restText) {
      const name = s.cols.find((c) => c.id === "name") ?? s.cols[0]!;
      out.push(cellHtml(s, { x: name.x, w: name.w, lines: [restText], size: t.size, bold: false, align: "left", color: t.color }, y, r.h, mode));
    }
    y += r.h;
  }
  // 線
  if (!pre) {
    // 縦の線：行ごとの欄の境。同じ境が続く行はまとめて1本
    const open = new Map<number, number>();
    const segs: string[] = [];
    const bounds = (cells: Cell[]): number[] => cells.slice(1).map((c) => r2(c.x));
    const step = (xs: number[], yAt: number) => {
      const set = new Set(xs);
      for (const [x, y0] of [...open]) if (!set.has(x)) { if (yAt > y0 && ruleOn(t.rules.v)) segs.push(vline(x, y0, yAt - y0, t.rules.v)); open.delete(x); }
      for (const x of xs) if (!open.has(x)) open.set(x, yAt);
    };
    if (headH) step(bounds(s.cols.map((c) => ({ x: c.x, w: c.w, lines: [], size: 0, bold: false, align: "left" as Align, color: "" }))), 0);
    let yy = headH;
    for (const r of part.rows) { step(bounds(r.cells), yy); yy += r.h; }
    step([], yy);
    out.push(...segs);
    // 横の線：行の上
    yy = headH;
    part.rows.forEach((r, i) => {
      const rule = i === 0 && headH ? null : r.top;
      if (rule && ruleOn(rule) && yy > 0) out.push(hline(0, yy, t.w, rule));
      yy += r.h;
    });
    // 見出しの下の線（合計の太い線が上に来る行はその線を優先）
    if (headH) {
      const first = part.rows[0];
      const rule = first && first.kind === "foot" && first.top && ruleOn(first.top) ? first.top : t.rules.head;
      if (ruleOn(rule)) out.push(hline(0, headH, t.w, rule));
    }
    // 外の枠
    if (ruleOn(t.rules.outer) && H > 0) out.push(frameBox(0, 0, t.w, H, t.rules.outer));
  }
  // 明細表の下の注記（T2：「※は軽減税率対象」）。表の下端の 3mm の中
  if (part.taxNote) {
    out.push(`<div class="tnote" style="position:absolute;left:0;top:${mm(H + 0.4)};width:100%;height:2.6mm;display:flex;align-items:center;overflow:hidden;font-family:${FONT_CSS.sans};font-size:7.5pt;line-height:1;color:${t.color || "#1A1A18"};padding:0 1mm;box-sizing:border-box;"><span style="white-space:nowrap">${esc(part.taxNote)}</span></div>`);
  }
  out.push(`</div>`);
  return { html: out.join(""), h: H };
}

/* ---------------------------------------------------------------- 部品 */

/** 項目の値。text は前後の文字つき・敬称なし。prefix は前に付けた文字、hon は最後の行の後ろに付ける敬称（宛名） */
type FieldText = { text: string; alt: boolean; prefix: string; hon: string };
function valueOf(e: PaperEl & { type: "field" }, data: PaperData, pageNo: number, pageCount: number): FieldText {
  // 見出しを刷る項目（#233 2d）はデータの見出し。請書の欄（ack）はデータの欄が無ければ空（＝描かない・詰める）
  const raw = e.head ? (data.heads?.[e.bind] ?? "") : e.bind === "ack" ? (data.ack ? data.ack.greet || data.ack.title : "") : e.bind === "pageNo" ? `${pageNo} / ${pageCount}` : (data.fields[e.bind] ?? "");
  let hon = "";
  if (data.hon && (e.bind === "toName" || e.bind === "toSub")) {
    const subEmpty = (data.fields.toSub ?? "") === "";
    if ((e.bind === "toSub" && !subEmpty) || (e.bind === "toName" && subEmpty)) hon = data.hon;
  }
  if (raw === "") {
    const alt = (data.alt as Record<string, string> | undefined)?.[e.bind];
    return alt ? { text: alt, alt: true, prefix: "", hon: "" } : { text: "", alt: false, prefix: "", hon: "" };
  }
  const prefix = e.prefix ?? "";
  return { text: `${prefix}${raw}${e.suffix ?? ""}`, alt: false, prefix, hon };
}

const SHORT: Record<string, string> = {
  docNo: "番号", docDate: "日付", toName: "宛名", toSub: "宛名の担当者", projectName: "工事名", site: "工事場所", period: "工期", outline: "工事概要", terms: "支払条件", valid: "有効期限", due: "お支払期限", staff: "担当",
  coName: "会社名", coRep: "代表者名", coAddr: "住所", coTel: "電話", coReg: "登録番号", coPerson: "自社担当", subtotal: "小計", tax: "消費税", total: "合計", taxByRate: "税率ごとの内訳", notes: "備考", bank: "振込先", installment: "分割の欄", pageNo: "ページ",
  proviso: "但し書き", invoiceNo: "請求書番号", docName: "帳票名",
  title: "題", greet: "挨拶文", receive: "受領期日", place: "納品場所", inspect: "検査完了期日", payOn: "支払期日", payMethod: "支払方法", clause: "約款の1行", negotiation: "変更協議", ack: "請書の欄",
};
const bindLabelShort = (b: string, labels?: Labels): string => {
  const own = labels ? ({ projectName: labels.subject, site: labels.site, period: labels.period, outline: labels.outline } as Record<string, string>)[b] : undefined;
  return `［${own ?? SHORT[b] ?? b}］`;
};

type TextLike = { font: Font; size: number; bold: boolean; align: Align; valign: "top" | "middle" | "bottom"; color: string; lh?: number | undefined; ls?: number | undefined; weight?: number | undefined };
function textStyle(e: TextLike, size = e.size): string {
  const va = { top: "flex-start", middle: "center", bottom: "flex-end" }[e.valign];
  const ha = { left: "flex-start", center: "center", right: "flex-end" }[e.align];
  return `font-family:${FONT_CSS[e.font]};font-size:${pt(size)};font-weight:${e.weight ?? (e.bold ? 700 : 400)};color:${e.color || "#1A1A18"};align-items:${va};justify-content:${ha};text-align:${e.align};line-height:${e.lh ?? DEFAULT_LH};${e.ls ? `letter-spacing:${e.ls}em;` : ""}`;
}

/** 敬称を付けた折り返し用の文字（敬称の見た目があるときは全角1字あけ、無ければ半角1字あけ） */
const honJoin = (e: PaperEl & { type: "field" }, ft: FieldText): string => (ft.hon ? `${ft.text}${e.hon ? "\u3000" : " "}${ft.hon}` : ft.text);

/** 項目の中身の行（自分で折る）。fit なら1行で文字を小さく */
function fieldLines(e: PaperEl & { type: "field" }, ft: FieldText): { lines: string[]; size: number } {
  const inner = Math.max(0.5, e.w - 1);
  const text = honJoin(e, ft);
  if (e.fit) return { lines: [text], size: fitSize(text, (e.weight ?? (e.bold ? 700 : 400)) >= 600 ? inner / 1.06 : inner, e.size, 5, e.font) };
  return { lines: wrapText(text, inner, e.size, e.font), size: e.size };
}

/**
 * 字の部品が実際に字を置く横の範囲（寄せ方と字の幅の見積もり。縦は枠のまま）。#223：通し番号の帯（下の中央）との重なりは、
 * 左寄せの「帳票名」・右寄せの「ページ番号」のように枠は帯にかかっても字はかからない部品を数えないよう、字の範囲で見る。
 * 字の無い部品（線・枠・画像・二次元コード）・中身の無い項目・下書きの代わりの文字は枠のまま
 */
export function inkBox(e: PaperEl, b: Box, data: PaperData): Box {
  if (e.type !== "text" && e.type !== "field") return b;
  let lines: string[];
  let size: number;
  if (e.type === "field") {
    const v = valueOf(e, data, 1, 1);
    if (!v.text || v.alt || (e.bind === "ack" && !e.head)) return b;
    const fl = fieldLines(e, v);
    lines = fl.lines;
    size = fl.size;
  } else {
    if (!e.text.trim()) return b;
    lines = wrapText(e.text, Math.max(0.5, b.w - 1), e.size, e.font);
    size = e.size;
  }
  const boldK = (e.weight ?? (e.bold ? 700 : 400)) >= 600 ? 1.06 : 1;
  const tw = Math.min(Math.max(b.w - 1, 0.1), Math.max(0, ...lines.map((l) => textWidthMm(l, size, e.font, e.ls ?? 0))) * boldK);
  const x = e.align === "right" ? b.x + b.w - 0.5 - tw : e.align === "center" ? b.x + (b.w - tw) / 2 : b.x + 0.5;
  return { x, y: b.y, w: tw, h: b.h };
}

/** 項目の行 → HTML（前の文字・敬称の見た目） */
function fieldInner(e: PaperEl & { type: "field" }, ft: FieldText, lines: string[]): string {
  const out = lines.map(esc);
  if (ft.prefix && (e.prefixSize || e.prefixFont) && lines[0]?.startsWith(ft.prefix)) {
    out[0] = `<span style="font-size:${pt(e.prefixSize ?? e.size)};font-family:${FONT_CSS[e.prefixFont ?? e.font]};font-weight:400">${esc(ft.prefix)}</span>${esc(lines[0].slice(ft.prefix.length))}`;
  }
  if (ft.hon && e.hon) {
    const i = out.length - 1;
    const last = lines[i] ?? "";
    const tail = `\u3000${ft.hon}`;
    const span = (m: string) => `<span style="font-size:${pt(e.hon!.size)};font-weight:400;margin-left:${m}">${esc(ft.hon)}</span>`;
    if (last.endsWith(tail)) out[i] = `${esc(last.slice(0, -tail.length))}${span(`${e.hon.gap}em`)}`;
    else if (last === ft.hon) out[i] = span("0");
  }
  return out.join("<br>");
}

/**
 * 請書の欄（#233 2d。項目 ack）：兼請書なら請書の頭（題・宛名＝発注者・番号・工事名・金額）→「上記のとおりお請けいたします。」→
 * 承諾の記録（電子）か署名欄（紙。空の欄と「印」）。部品の枠の中に描き、はみ出す分は切る（枠は書式で大きさを決める）
 */
function ackHtml(e: PaperEl & { type: "field" }, a: NonNullable<PaperData["ack"]>, pre: boolean): string {
  const size = e.size;
  const small = Math.max(6, size - 1.5);
  const line = pre ? "transparent" : "#1A1A18";
  const cell = `border-bottom:0.2mm solid ${pre ? "transparent" : "#8C8880"};padding:0.4mm 1mm;vertical-align:middle;`;
  const rows = (xs: Array<[string, string]>, sign: boolean) =>
    xs
      .map(
        ([k, v], i) =>
          `<tr><th style="${cell}width:${sign ? 16 : 20}mm;text-align:left;font-weight:400;color:#444444;font-size:${pt(small)};white-space:nowrap">${esc(k)}</th><td style="${cell}${sign ? "height:5.5mm;" : ""}${sign && i === xs.length - 1 ? "text-align:right;color:#888888;" : ""}${v.startsWith("SHA-256 ") ? `font-family:'Noto Sans Mono CJK JP',monospace;font-size:${pt(Math.max(5, small - 1.5))};word-break:break-all;` : ""}">${esc(v)}</td></tr>`,
      )
      .join("");
  const greet = `<div style="font-weight:600;font-size:${pt(size + 1)};margin:0 0 1mm">${esc(a.greet)}</div>`;
  const box = `<div style="border:0.26mm solid ${line};padding:1mm 2mm"><div style="font-weight:600;margin:0 0 0.5mm">${esc(a.title)}</div><table style="border-collapse:collapse;width:100%">${rows(a.rows, a.kind === "paper")}</table></div>`;
  if (!a.head) {
    // 幅のある欄（150mm 以上）は 左に「上記のとおり…」と欄の名前、右に記録・署名欄（高さを詰める）。狭い欄は上から順に
    if (e.w < 150) return greet + box;
    return (
      `<div style="display:flex;gap:4mm;align-items:flex-start"><div style="flex:0 0 38%;min-width:0">${greet}<div style="font-weight:600;color:#444444;font-size:${pt(small)}">${esc(a.title)}</div></div>` +
      `<div style="flex:1 1 0;min-width:0;border:0.26mm solid ${line};padding:0.6mm 2mm"><table style="border-collapse:collapse;width:100%">${rows(a.rows, a.kind === "paper")}</table></div></div>`
    );
  }
  // 兼請書：題（中央）→ 左に 宛名（発注者）・番号・工事名・金額・「上記のとおり…」、右に署名欄（記録）
  return (
    `<div style="font-family:${FONT_CSS.serif};font-size:${pt(size + 4)};font-weight:600;text-align:center;letter-spacing:0.3em;margin:0 0 1.5mm">${esc(a.head.title)}</div>` +
    `<div style="display:flex;gap:4mm;align-items:flex-start"><div style="flex:1 1 0;min-width:0">` +
    `<div style="font-weight:600;font-size:${pt(size + 1)};margin:0 0 1mm">${esc(a.head.to)}</div>` +
    `<table style="border-collapse:collapse;width:100%;margin:0 0 1.5mm">${rows(a.head.rows, false)}</table>${greet}</div>` +
    `<div style="flex:1 1 0;min-width:0">${box}</div></div>`
  );
}

function elHtml(e: PaperEl, b: Box, data: PaperData, pageNo: number, pageCount: number, mode: RenderMode, pre: boolean, assets: Record<string, string>, labels?: Labels): string {
  const hidden = !e.on || (pre && e.frame) || (e.type === "image" && e.guide);
  if (hidden && mode === "print") return "";
  const attrs = mode === "edit" ? ` data-id="${esc(e.id)}" data-type="${e.type}"${hidden ? ' data-off="1"' : ""}` : "";
  const dim = hidden ? "opacity:.28;outline:0.3mm dashed #9E2718;" : "";
  const pos = `position:absolute;left:${mm(b.x)};top:${mm(b.y)};width:${mm(Math.max(b.w, 0.1))};height:${mm(Math.max(b.h, 0.1))};`;
  switch (e.type) {
    case "text":
      return `<div class="el"${attrs} style="${pos}${dim}display:flex;overflow:hidden;padding:0 0.5mm;${textStyle(e)}"><span style="white-space:pre-wrap;overflow-wrap:anywhere">${esc(e.text).replace(/\r?\n/g, "<br>")}</span></div>`;
    case "field": {
      const v = valueOf(e, data, pageNo, pageCount);
      if (mode === "edit" && v.text === "") {
        return `<div class="el"${attrs} style="${pos}${dim}display:flex;overflow:hidden;padding:0 0.5mm;${textStyle(e)}"><span style="color:#9E2718;opacity:.6;white-space:nowrap">${esc(bindLabelShort(e.bind, labels))}</span></div>`;
      }
      if (e.bind === "ack" && data.ack && !e.head) {
        return `<div class="el ack"${attrs} style="${pos}${dim}overflow:hidden;padding:0 0.5mm;font-family:${FONT_CSS[e.font]};font-size:${pt(e.size)};line-height:1.4;color:${e.color || "#1A1A18"};">${ackHtml(e, data.ack, pre)}</div>`;
      }
      if (v.alt) {
        // 下書きの番号の欄など：小さめのゴシック・1行・枠の外へ出てよい
        const size = Math.min(e.size, 10);
        return `<div class="el"${attrs} style="${pos}${dim}display:flex;overflow:visible;padding:0 0.5mm;${textStyle({ ...e, font: "sans", bold: false }, size)}"><span style="white-space:nowrap">${esc(v.text)}</span></div>`;
      }
      const fl = fieldLines(e, v);
      return `<div class="el"${attrs} style="${pos}${dim}display:flex;overflow:${e.fit ? "visible" : "hidden"};padding:0 0.5mm;${textStyle(e, fl.size)}"><span style="white-space:pre">${fieldInner(e, v, fl.lines)}</span></div>`;
    }
    case "line": {
      const rule: PaperRule = { style: e.style, w: e.lw, color: e.color };
      if (rule.style === "none") return mode === "edit" ? `<div class="el"${attrs} style="${pos}${dim}"></div>` : "";
      const lw = ruleW(rule);
      const css = e.dir === "v" ? `left:${mm(b.x - lw / 2)};top:${mm(b.y)};width:0;height:${mm(b.h)};border-left:${mm(lw)} ${rule.style} ${rule.color};` : `left:${mm(b.x)};top:${mm(b.y - lw / 2)};height:0;width:${mm(b.w)};border-top:${mm(lw)} ${rule.style} ${rule.color};`;
      return `<div class="el"${attrs} style="position:absolute;${css}${dim}"></div>`;
    }
    case "box": {
      const border = e.style === "none" ? "border:0;" : `border:${mm(e.style === "double" ? Math.max(e.lw, DBL_MIN) : e.lw)} ${e.style} ${e.color};`;
      return `<div class="el"${attrs} style="${pos}${dim}${border}background:${e.fill || "transparent"};box-sizing:border-box;"></div>`;
    }
    case "image": {
      const src = e.asset ? assets[e.asset] : undefined;
      if (!src) {
        if (mode !== "edit") return "";
        const label = e.asset === "company:logo" ? "［ロゴ］" : e.asset === "company:seal" ? "［社印］" : `［${e.name || "画像"}］`;
        return `<div class="el"${attrs} style="${pos}${dim}border:0.3mm dashed #9E2718;display:flex;align-items:center;justify-content:center;font-size:7pt;color:#9E2718;text-align:center;opacity:.75">${esc(label)}</div>`;
      }
      const fit = e.keep ? "object-fit:contain;object-position:left top;" : "object-fit:fill;";
      const guideMark = e.guide && mode === "edit" ? `<span style="position:absolute;left:0;top:0;font-size:6.5pt;color:#9E2718;background:rgba(255,255,255,.8);padding:0 1mm;line-height:1.4">下敷き（印刷しません）</span>` : "";
      // 重なりを許可した画像（社印）は下の文字が透けるように掛け合わせる（今の紙と同じ）
      const blend = e.overlap ? "mix-blend-mode:multiply;" : "";
      return `<div class="el img"${attrs}${e.guide && mode === "edit" ? ' data-guide="1"' : ""} style="${pos}${dim}overflow:hidden;${blend}"><img src="${esc(src)}" alt="" draggable="false" style="display:block;width:100%;height:100%;${fit}opacity:${e.opacity};">${guideMark}</div>`;
    }
    case "qr": {
      const content = qrContent(e, data.fields);
      const size = Math.min(b.w, b.h);
      const svg = content ? qrSvg(content, size) : "";
      if (!svg) return mode === "edit" ? `<div class="el"${attrs} style="${pos}${dim}border:0.3mm dashed #8C8880;display:flex;align-items:center;justify-content:center;font-size:7pt;color:#55534C;text-align:center">二次元コード<br>（中身を入れてください）</div>` : "";
      return `<div class="el qr"${attrs} style="${pos}${dim}overflow:hidden;background:#fff;">${svg}</div>`;
    }
  }
}

/* ---------------------------------------------------------------- 詰める組（stack） */

type Placed = { box: Box; skip: boolean };

/** 詰める組の部品の中身：空か、書式の高さからの増減（項目は 折った行の数 − 書式の高さに入る行の数 × 行の高さ） */
function contentDelta(e: PaperEl, data: PaperData, assets: Record<string, string>): { empty: boolean; delta: number } {
  if (e.type === "field") {
    const v = valueOf(e, data, 1, 1);
    if (v.text === "") return { empty: true, delta: 0 };
    if (e.bind === "ack" && !e.head) return { empty: false, delta: 0 };
    if (v.alt || e.fit) return { empty: false, delta: 0 };
    const fl = fieldLines(e, v);
    const lh = lineMm(fl.size, e.lh ?? DEFAULT_LH);
    const n0 = Math.max(1, Math.round(e.h / lh));
    return { empty: false, delta: (fl.lines.length - n0) * lh };
  }
  if (e.type === "image") return { empty: !(e.asset && assets[e.asset]) || !e.on, delta: 0 };
  return { empty: false, delta: 0 };
}

/**
 * 1ページ目の部品の位置（印刷）。詰める組ごとに上から並べ、空の行は詰め、伸びた行の分だけ下を押す。
 * 戻り値の shift は「組の下端がいちばん低い所」の動いた量（明細表を同じだけ動かす）
 */
function placeFirstPage(layout: PaperLayout, data: PaperData, assets: Record<string, string>, mode: RenderMode): { placed: Map<string, Placed>; shift: number } {
  const placed = new Map<string, Placed>();
  for (const e of layout.els) if (!e.dock) placed.set(e.id, { box: { x: e.x, y: e.y, w: e.w, h: e.h }, skip: false });
  if (mode === "edit") return { placed, shift: 0 };
  const stacks = new Map<string, PaperEl[]>();
  for (const e of layout.els) if (e.stack && !e.dock && !(e.type === "image" && e.guide)) (stacks.get(e.stack) ?? stacks.set(e.stack, []).get(e.stack)!).push(e);
  let designBottom = -Infinity;
  let shownBottom = -Infinity;
  const t = layout.table;
  for (const els of stacks.values()) {
    const isVLine = (e: PaperEl) => e.type === "line" && e.dir === "v";
    const rowEls = els.filter((e) => !isVLine(e) && !e.overlap && e.on);
    const ys = [...new Set(rowEls.map((e) => Math.round(e.y * 10) / 10))].sort((a, b) => a - b);
    const rows = ys.map((y) => ({ y, els: rowEls.filter((e) => Math.abs(e.y - y) < 0.06), delta: 0, collapse: false }));
    rows.forEach((r, i) => {
      const info = r.els.map((e) => ({ e, ...contentDelta(e, data, assets) }));
      const candidates = info.filter((x) => x.e.type === "field" || x.e.type === "image");
      r.collapse = candidates.length > 0 && candidates.length === info.length && info.every((x) => x.empty);
      if (r.collapse) {
        // 画像の行（ロゴ）は下のあきごと詰める（今の紙の画像の下の余白）。項目の行（宛名の担当者）は高さだけ詰める
        const next = rows[i + 1];
        const h = Math.max(...r.els.map((e) => e.h));
        r.delta = r.els.some((e) => e.type === "image") && next ? -(next.y - r.y) : -h;
      } else {
        const ds = info.filter((x) => x.e.type === "field" && !x.empty).map((x) => x.delta);
        r.delta = ds.length ? Math.max(...ds) : 0;
      }
    });
    const before = (y: number) => rows.filter((r) => r.y < y - 0.05).reduce((a, r) => a + r.delta, 0);
    const within = (y0: number, y1: number) => rows.filter((r) => r.y >= y0 - 0.05 && r.y < y1 - 0.05).reduce((a, r) => a + r.delta, 0);
    for (const r of rows) {
      const sh = before(r.y);
      for (const e of r.els) {
        const p = placed.get(e.id)!;
        if (r.collapse) { p.skip = true; continue; }
        const grow = e.type === "text" || e.type === "field" ? r.delta : 0;
        p.box = { x: e.x, y: e.y + sh, w: e.w, h: Math.max(0.5, e.h + grow) };
      }
    }
    for (const e of els) {
      if (rowEls.includes(e)) continue;
      const p = placed.get(e.id)!;
      const sh = before(e.y);
      p.box = { ...p.box, y: e.y + sh, h: isVLine(e) ? e.h + within(e.y, e.y + e.h) : e.h };
    }
    // 明細表より上の組だけ、表を動かす量に数える
    const dBottom = Math.max(...els.map((e) => e.y + (e.type === "line" && e.dir !== "v" ? 0 : e.h)));
    if (dBottom <= t.y + 0.5) {
      designBottom = Math.max(designBottom, dBottom);
      const sBottom = Math.max(...els.map((e) => { const p = placed.get(e.id)!; return p.skip ? -Infinity : p.box.y + (e.type === "line" && e.dir !== "v" ? 0 : p.box.h); }));
      shownBottom = Math.max(shownBottom, sBottom);
    }
  }
  let shift = Number.isFinite(designBottom) && Number.isFinite(shownBottom) ? shownBottom - designBottom : 0;
  // 上へ寄せるとき、組でない部品（明細表より上）を越えない
  if (shift < 0) {
    let floor = -Infinity;
    for (const e of layout.els) {
      if (e.stack || e.dock || !e.on || (e.type === "image" && e.guide)) continue;
      const bottom = e.y + (e.type === "line" && e.dir !== "v" ? 0 : e.h);
      if (bottom <= t.y + 0.05) floor = Math.max(floor, bottom + 2);
    }
    if (Number.isFinite(floor)) shift = Math.max(shift, Math.min(0, floor - t.y));
  }
  return { placed, shift };
}

/* ---------------------------------------------------------------- 枠（備考など）と内訳の見出し */

type BoxSpec = { title: string; lines: string[]; h: number };
function boxSpecs(t: PaperTable, boxes: PaperBox[] | undefined): BoxSpec[] {
  const B = t.boxes;
  if (!B?.on || !boxes?.length) return [];
  const lh = lineMm(B.size, 1.45);
  const pad = 2;
  const bw = ruleW(B.rule);
  return boxes
    .filter((b) => b.text.trim() !== "")
    .map((b) => {
      const lines = wrapText(b.text, t.w - 6 - 2 * bw, B.size);
      const h = Math.max(B.minH, 2 * bw + 2 * pad + lh + 1 + lines.length * lh);
      return { title: b.title, lines, h };
    });
}
function boxHtml(t: PaperTable, x: number, y: number, b: BoxSpec, pre: boolean): string {
  const B = t.boxes!;
  const lh = lineMm(B.size, 1.45);
  // 専用帳票モードでは枠の線と見出しを刷らず、本文だけ
  const rule = ruleOn(B.rule) && !pre ? `border:${mm(ruleW(B.rule))} ${B.rule.style} ${B.rule.color};` : "border:0;";
  if (pre) return `<div class="tbox" style="position:absolute;left:${mm(x)};top:${mm(y)};width:${mm(t.w)};height:${mm(b.h)};${rule}box-sizing:border-box;padding:2mm 3mm;overflow:hidden;font-family:${FONT_CSS.sans};font-size:${pt(B.size)};line-height:${mm(lh)};color:${B.color};"><div style="height:${mm(lh)};margin-bottom:1mm"></div><div style="white-space:pre">${b.lines.map(esc).join("<br>")}</div></div>`;
  return `<div class="tbox" style="position:absolute;left:${mm(x)};top:${mm(y)};width:${mm(t.w)};height:${mm(b.h)};${rule}box-sizing:border-box;padding:2mm 3mm;overflow:hidden;font-family:${FONT_CSS.sans};font-size:${pt(B.size)};line-height:${mm(lh)};color:${B.color};"><div style="font-weight:700;margin-bottom:1mm">${esc(b.title)}</div><div style="white-space:pre">${b.lines.map(esc).join("<br>")}</div></div>`;
}

/**
 * 内訳の見出しの見た目。書式に無ければ（#217 より前に保存したカスタム書式）標準のプリセットと同じ（明朝 14pt・二重線）。
 * 内訳の紙は書式の設定の有無に関わらず描く＝表紙の表に内訳の明細を混ぜない（#217 の不具合、2026-10-19 本番）
 */
export const DEFAULT_SECTIONS: PaperTableSections = { font: "serif", size: 14, color: "#1A1A1A", rule: { style: "double", w: 0.79, color: "#1A1A1A" }, refSize: 9, refColor: "#444444", gap: 9 };
const secSpec = (t: PaperTable): PaperTableSections => t.sections ?? DEFAULT_SECTIONS;

function headingH(t: PaperTable): number {
  const S = secSpec(t);
  // h2 の下の余白 4mm と表の上の余白 2mm は重なって 4mm（今の紙と同じ）
  return lineMm(S.size, 1.45) + 1.5 + ruleW(S.rule) + 4;
}
function headingHtml(t: PaperTable, x: number, y: number, sec: PaperSection, anchor: string, pre: boolean): string {
  const S = secSpec(t);
  const lh = lineMm(S.size, 1.45);
  const rule = ruleOn(S.rule) && !pre ? hline(0, lh + 1.5 + ruleW(S.rule) / 2, t.w, S.rule) : "";
  return `<div id="${esc(anchor)}" style="position:absolute;left:${mm(x)};top:${mm(y)};width:${mm(t.w)};height:${mm(lh + 1.5 + ruleW(S.rule))};">`
    + `<div style="position:absolute;left:0;top:0;width:100%;height:${mm(lh)};display:flex;align-items:baseline;gap:8mm;white-space:nowrap;overflow:hidden;">`
    + `<span style="flex:1 1 auto;min-width:0;overflow:hidden;font-family:${FONT_CSS[S.font]};font-weight:600;font-size:${pt(S.size)};line-height:${mm(lh)};color:${S.color}">${esc(sec.title)}</span>`
    + `<span style="flex:0 0 auto;font-family:${FONT_CSS[S.font]};font-size:${pt(S.refSize)};color:${S.refColor}">${esc(sec.ref)}</span></div>${rule}</div>`;
}

function beforeH(data: PaperData): number {
  return data.before ? 2 * (lineMm(9.5, 1.45) + 2 + 0.26) + 3 : 0;
}
function beforeHtml(t: PaperTable, y: number, b: NonNullable<PaperData["before"]>, pre: boolean): string {
  const rh = lineMm(9.5, 1.45) + 2 + 0.26;
  const n = Math.max(1, b.heads.length);
  const cw = t.w / n;
  const out: string[] = [`<div style="position:absolute;left:${mm(t.x)};top:${mm(y)};width:${mm(t.w)};height:${mm(rh * 2)};">`];
  if (!pre) out.push(`<div style="position:absolute;left:0;top:0;width:100%;height:${mm(rh)};background:#EFEEE9"></div>`);
  b.heads.forEach((h, i) => {
    const base = `position:absolute;left:${mm(i * cw)};width:${mm(cw)};height:${mm(rh)};box-sizing:border-box;padding:1mm 2mm;font-family:${FONT_CSS.sans};font-size:9.5pt;line-height:1.45;white-space:nowrap;overflow:hidden;`;
    if (!pre) out.push(`<div style="${base}top:0;text-align:center;font-weight:600;">${esc(h)}</div>`);
    out.push(`<div style="${base}top:${mm(rh)};text-align:${b.align ?? "right"};font-weight:${b.strong === i ? 700 : 400};">${esc(b.vals[i] ?? "")}</div>`);
    if (i > 0 && !pre) out.push(vline(i * cw, 0, rh * 2, { style: "solid", w: 0.26, color: "#1A1A18" }));
  });
  if (!pre) {
    out.push(hline(0, rh, t.w, { style: "solid", w: 0.26, color: "#1A1A18" }));
    out.push(frameBox(0, 0, t.w, rh * 2, { style: "solid", w: 0.26, color: "#1A1A18" }));
  }
  out.push(`</div>`);
  return out.join("");
}

/**
 * 「お支払い予定」の表（#267 3b：分割払いの請求書だけ。明細表の上、before の下）。左寄せで幅 100mm まで。
 *   1行目＝題（地色）、回ごとに「第1回｜2026年11月30日｜475,676 円」、最後に「合計」。専用帳票モードは値だけ
 */
const SCHED_W = 100;
function scheduleH(data: PaperData): number {
  const sc = data.schedule;
  return sc ? (sc.rows.length + 2) * (lineMm(9.5, 1.45) + 2 + 0.26) + 3 : 0;
}
function scheduleHtml(t: PaperTable, y: number, sc: NonNullable<PaperData["schedule"]>, pre: boolean): string {
  const rh = lineMm(9.5, 1.45) + 2 + 0.26;
  const w = Math.min(t.w, SCHED_W);
  const cols = [0, 0.2 * w, 0.62 * w, w];
  const n = sc.rows.length + 2;
  const rule = { style: "solid" as const, w: 0.26, color: "#1A1A18" };
  const out: string[] = [`<div style="position:absolute;left:${mm(t.x)};top:${mm(y)};width:${mm(w)};height:${mm(rh * n)};">`];
  const cell = (row: number, from: number, to: number, text: string, align: "left" | "right", bold = false) =>
    `<div style="position:absolute;left:${mm(cols[from]!)};top:${mm(row * rh)};width:${mm(cols[to]! - cols[from]!)};height:${mm(rh)};box-sizing:border-box;padding:1mm 2mm;font-family:${FONT_CSS.sans};font-size:9.5pt;line-height:1.45;white-space:nowrap;overflow:hidden;text-align:${align};font-weight:${bold ? 700 : 400};">${esc(text)}</div>`;
  if (!pre) {
    out.push(`<div style="position:absolute;left:0;top:0;width:100%;height:${mm(rh)};background:#EFEEE9"></div>`);
    out.push(cell(0, 0, 3, sc.title, "left", true));
  }
  sc.rows.forEach((r, i) => {
    out.push(cell(i + 1, 0, 1, r.label, "left"), cell(i + 1, 1, 2, r.date, "left"), cell(i + 1, 2, 3, r.amount, "right"));
  });
  out.push(cell(n - 1, 0, 2, sc.total.label, "left"), cell(n - 1, 2, 3, sc.total.amount, "right", true));
  if (!pre) {
    for (let i = 1; i < n; i++) out.push(hline(0, i * rh, w, rule));
    out.push(frameBox(0, 0, w, rh * n, rule));
  }
  out.push(`</div>`);
  return out.join("");
}

/* ---------------------------------------------------------------- ページに割る */

/** boxesHere：最後のページの表の下に入る枠の数（先頭から。残りは次のページ） */
type Paginated = { pages: Array<{ rows: RowBox[] }>; boxesHere: number };

/**
 * 行をページに割る。tail（合計の行・内訳の合計）は最後の行と同じページ。最後のページは fill なら空行で下まで埋める
 *   firstCap：1ページ目の行の枠の高さを決める（「ページの下まで空行で埋める」が OFF のとき＝前の書式と同じ 1ページ目は rows 行）
 */
function paginateRows(rows: RowBox[], tail: RowBox[], o: {
  firstTop: number; nextTop: number; bottom: number; headH: number; reserve: number; boxesH: number[]; fill: boolean; firstCap?: number | undefined;
  blank: (h: number, rest: boolean) => RowBox; pitch: number;
}): Paginated {
  const pages: Array<{ rows: RowBox[] }> = [];
  const tailH = tail.reduce((a, r) => a + r.h, 0);
  let i = 0;
  let top = o.firstTop;
  let first = true;
  for (let guard = 0; guard < 500; guard++) {
    const area = o.bottom - top - o.headH;
    const cap = first && !o.fill && o.firstCap != null ? o.firstCap : undefined;
    const rest = rows.slice(i);
    const restH = rest.reduce((a, r) => a + r.h, 0);
    const fits = cap != null ? restH <= cap + 1e-6 && cap + tailH + o.reserve <= area + 1e-6 : restH + tailH + o.reserve <= area + 1e-6;
    if (fits) {
      const used = cap != null ? cap : restH;
      // 表の下に入る枠（先頭から順に。入らない枠から後ろは次のページ）
      let boxesHere = 0;
      let boxesH = 0;
      for (const bh of o.boxesH) {
        if (used + tailH + o.reserve + boxesH + bh > area + 1e-6) break;
        boxesH += bh;
        boxesHere++;
      }
      let blanksArea = 0;
      if (cap != null) blanksArea = cap - restH;
      else if (o.fill) blanksArea = area - restH - tailH - o.reserve - boxesH;
      const n = blanksArea > 0 ? Math.floor((blanksArea + 1e-6) / o.pitch) : 0;
      const extra = n > 0 && cap == null ? Math.min((blanksArea - n * o.pitch) / n, 1) : 0;
      const blanks = Array.from({ length: n }, (_, k) => o.blank(o.pitch + extra, k === 0));
      pages.push({ rows: [...rest, ...blanks, ...tail] });
      return { pages, boxesHere };
    }
    const limit = cap ?? area;
    let k = 0;
    let acc = 0;
    while (i + k < rows.length && acc + rows[i + k]!.h <= limit + 1e-6) acc += rows[i + k++]!.h;
    if (i + k === rows.length && k >= 2) k -= 1; // 最後の行は合計と同じページへ
    if (k === 0 && i < rows.length) k = 1; // 1行がページより高い（切れる）
    pages.push({ rows: rows.slice(i, i + k) });
    i += k;
    first = false;
    top = o.nextTop;
  }
  pages.push({ rows: [...rows.slice(i), ...tail] });
  return { pages, boxesHere: 0 };
}

/* ---------------------------------------------------------------- 全体 */

/** endY：枠（備考など）のあとに付ける部品（dock end、#233 2d）の基準（その下 3mm から dy）。そのページだけ */
type DrawPage = { first: boolean; parts: TablePart[]; headings: Array<{ y: number; sec: PaperSection; anchor: string }>; boxes: Array<{ y: number; b: BoxSpec }>; mainLast: boolean; endY?: number };

/** 2ページ目からも出す部品。repeat を決めた部品が1つでもあれば repeat の部品、無ければ前の決まり（番号・日付・ページ番号とその組） */
function repeatEls(layout: PaperLayout): PaperEl[] {
  if (layout.page.secondPage === "same") return layout.els.filter((e) => !e.dock);
  if (layout.els.some((e) => e.repeat !== undefined)) return layout.els.filter((e) => e.repeat === true && !e.dock);
  const groups = new Set<string>();
  for (const e of layout.els) if (e.type === "field" && (e.bind === "docNo" || e.bind === "docDate") && e.group) groups.add(e.group);
  return layout.els.filter((e) => !e.dock && ((e.type === "field" && (e.bind === "pageNo" || e.bind === "docNo" || e.bind === "docDate")) || (e.group !== undefined && groups.has(e.group))));
}

/** 2ページ目からも出す部品の id（エディタの「2ページ目以降にも出す」の印の表示） */
export function repeatIds(layout: PaperLayout): Set<string> {
  return new Set(repeatEls(layout).map((e) => e.id));
}

/**
 * 「2ページ目以降にも出す」を付ける・外す（エディタ）。印の無い前の書式（#217 より前のカスタム書式）は前の決まりで出している部品
 * （番号・日付とその組・ページ番号）を先に印へ写してから変える＝1つ変えただけで ほかの部品が2ページ目から消えないように
 */
export function withRepeat(layout: PaperLayout, id: string, on: boolean): PaperLayout {
  const explicit = layout.els.some((e) => e.repeat !== undefined);
  const cur = repeatIds(layout);
  return {
    ...layout,
    els: layout.els.map((e) => {
      if (e.id === id) return { ...e, repeat: on };
      if (explicit || e.dock) return e;
      return { ...e, repeat: cur.has(e.id) };
    }),
  };
}

/**
 * 表・枠の下に付ける部品の置き方（#233 2d）：{order}の書式（印刷）だけ、中身が何行にもなる項目（変更協議・約款の1行）はその分伸ばし、
 * それより下（dy の大きい）の部品を同じだけ下げる（法令の文を枠で切らない）。ほかの帳票の書式は今のまま（伸ばさない）
 */
type DockPlace = Map<string, { dy: number; h: number }>;
function dockPlan(layout: PaperLayout, els: PaperEl[], data: PaperData, assets: Record<string, string>, mode: RenderMode): { place: DockPlace; h: number } {
  const place: DockPlace = new Map();
  const grows = layout.kind === "order" && mode === "print";
  // 中身の無い項目（仕入の{order}の約款の1行・変更協議）は高さを 0 にして下を詰める
  const deltas = els.map((e) => {
    if (!grows || e.type !== "field") return { e, d: 0, empty: false };
    const c = contentDelta(e, data, assets);
    return c.empty ? { e, d: -e.h, empty: true } : { e, d: Math.max(0, c.delta), empty: false };
  });
  let h = 0;
  for (const { e, d, empty } of deltas) {
    const dy0 = e.dy ?? 0;
    const shift = deltas.reduce((a, x) => a + ((x.e.dy ?? 0) < dy0 - 0.05 ? x.d : 0), 0);
    const eh = e.type === "line" && e.dir !== "v" ? 0 : e.h + d;
    place.set(e.id, { dy: dy0 + shift, h: e.type === "line" ? e.h : e.h + d });
    if (e.on && !empty) h = Math.max(h, dy0 + shift + eh);
  }
  return { place, h };
}

/** dock の部品がぜんぶ入るのに要る高さ（表の下端＋3mm から） */
export function dockHeight2(layout: PaperLayout): number {
  let h = 0;
  for (const e of layout.els) {
    if (e.dock !== "table" || !e.on) continue;
    const b = e.type === "line" && e.dir !== "v" ? 0 : e.h;
    h = Math.max(h, (e.dy ?? 0) + b);
  }
  return h;
}

/** 割り付けの結果（テストと見くらべ用）：ページごとの表（行の種類・高さ・名称）・枠・内訳の見出し */
export type PlanRow = { kind: "row" | "blank" | "foot"; h: number; text: string; rest: boolean };
export type PlanPage = { first: boolean; parts: Array<{ top: number; bottom: number; rows: PlanRow[] }>; boxes: Array<{ y: number; h: number; title: string }>; headings: Array<{ y: number; title: string }> };
export type Render2Result = { pages: string[]; geom: Geometry; pageCount: number; plan: PlanPage[]; bottom: number };

export function renderPages2(layout: PaperLayout, data: PaperData, opts: Render2Options = {}): Render2Result {
  const mode = opts.mode ?? "print";
  const assets = opts.assets ?? {};
  const t = layout.table;
  const pg = layout.page;
  const pre = pg.preprinted;
  const s = tableSpec(t);
  const fill = t.fill !== false;
  const bottom = pg.h - pg.margin[2];
  const tablePlan = dockPlan(layout, layout.els.filter((e) => e.dock === "table"), data, assets, mode);
  const dockH = layout.kind === "order" ? tablePlan.h : dockHeight2(layout);
  const { placed, shift } = placeFirstPage(layout, data, assets, mode);
  const bH = beforeH(data) + scheduleH(data);
  const firstTableTop = t.y + shift + bH;
  // 2ページ目からの表の上。「番号と表だけ」（compact）は、「2ページ目以降にも出す」の印で表より上に出す部品（番号・日付など）を上の余白まで寄せ
  // （左右の位置は表紙と同じ）、表と内訳の見出しはその下 3mm から（2026-10-19 射場さんの決定：2ページ目以降にも番号・日付）。
  // 印の無い前の書式（前の決まりで番号・日付を出す）は寄せない＝保存済みのカスタム書式の紙は変えない。表より上に出す部品が無ければ上の余白から
  let nextTop = pg.margin[0];
  const topRep = new Set<string>();
  let repShift = 0;
  const bottomOf = (e: PaperEl) => e.y + (e.type === "line" && e.dir !== "v" ? 0 : e.h);
  if (pg.secondPage === "same") nextTop = t.y;
  else {
    const tops = repeatEls(layout).filter((e) => bottomOf(e) <= t.y + 0.05);
    if (tops.length && layout.els.some((e) => e.repeat !== undefined)) repShift = Math.max(0, Math.min(...tops.map((e) => e.y)) - pg.margin[0]);
    for (const e of tops) {
      topRep.add(e.id);
      nextTop = Math.max(nextTop, Math.round((bottomOf(e) - repShift + 3) * 10) / 10);
    }
  }

  // 内訳の紙の始まるページ（「別紙 (P. n)」）：はじめは仮の 00 で測る
  const secStart = new Map<string, number>();
  const refLabel = (ref: string) => ` (P. ${secStart.get(ref) ?? "00"})`;
  const sections = data.sections?.length ? data.sections : [];
  const coverRows = data.rows;
  const footRows = t.foot?.on && data.foot?.length ? data.foot.map((f, i) => footOf(t, s, f, i === 0)) : [];
  const boxes = boxSpecs(t, data.boxes);
  const boxGap = t.boxes?.gap ?? 4;
  const boxesHs = boxes.map((b) => b.h + boxGap);
  const boxesH = boxesHs.reduce((a, b) => a + b, 0);
  const noteH = data.taxNote ? 3 : 0;
  // 枠（備考など）のあとに付ける部品（dock end、#233 2d：{order}兼請書の切り取り線と請書の欄）。請書の欄は中身が無ければ数えない（{order}の紙）
  const endEls = layout.els.filter((e) => e.dock === "end" && e.on && !(mode === "print" && e.type === "field" && e.bind === "ack" && !e.head && !data.ack));
  const endPlan = dockPlan(layout, endEls, data, assets, mode);
  const endH = endPlan.h;
  const reserveTable = dockH > 0 ? 3 + dockH : noteH;
  const reserve = reserveTable + (endH > 0 ? 3 + endH : 0);
  const blank = (h: number, rest: boolean) => blankOf(t, s, h, rest);
  const firstCap = opts.noBlank ? undefined : fill ? undefined : t.rows * t.pitch;
  const fillMain = fill && !opts.noBlank;

  /** エディタで内訳の紙の始まるページを測るときだけ true（刷るときと同じ割り付けを1回流す） */
  let measuring = false;
  const build = (): { draw: DrawPage[] } => {
    const rows = coverRows.map((r, i) => rowOf(t, s, r, refLabel, i % 2 === 1));
    const draw: DrawPage[] = [];
    if (mode === "edit" && !measuring) {
      // エディタ：1ページ目だけ。入るだけの行＋空行＋合計＋枠（入らない行は出さない）
      const area = bottom - firstTableTop - s.headH;
      const tailH = footRows.reduce((a, r) => a + r.h, 0);
      const limit = (firstCap ?? area - tailH - reserve - boxesH);
      const shown: RowBox[] = [];
      let acc = 0;
      for (const r of rows) { if (acc + r.h > limit + 1e-6) break; shown.push(r); acc += r.h; }
      const blanksArea = firstCap != null ? firstCap - acc : fill ? Math.max(0, limit - acc) : 0;
      const n = blanksArea > 0 ? Math.floor((blanksArea + 1e-6) / t.pitch) : 0;
      const extra = n > 0 && firstCap == null ? Math.min((blanksArea - n * t.pitch) / n, 1) : 0;
      const part: TablePart = { top: firstTableTop, rows: [...shown, ...Array.from({ length: n }, (_, k) => blank(t.pitch + extra, k === 0)), ...footRows], header: true, ...(data.taxNote ? { taxNote: data.taxNote } : {}) };
      const tableBottom = firstTableTop + s.headH + part.rows.reduce((a, r) => a + r.h, 0);
      let by = tableBottom + reserveTable + boxGap;
      const bx: Array<{ y: number; b: BoxSpec }> = [];
      for (const b of boxes) { bx.push({ y: by, b }); by += b.h + boxGap; }
      const lastBx = bx[bx.length - 1];
      draw.push({ first: true, parts: [part], headings: [], boxes: bx, mainLast: true, ...(endH > 0 ? { endY: lastBx ? lastBx.y + lastBx.b.h : tableBottom + reserveTable } : {}) });
      return { draw };
    }
    const main = paginateRows(rows, footRows, { firstTop: firstTableTop, nextTop, bottom, headH: s.headH, reserve, boxesH: boxesHs, fill: fillMain, firstCap, blank, pitch: t.pitch });
    main.pages.forEach((p, i) => {
      const last = i === main.pages.length - 1;
      draw.push({ first: i === 0, parts: [{ top: i === 0 ? firstTableTop : nextTop, rows: p.rows, header: true, ...(last && data.taxNote ? { taxNote: data.taxNote } : {}) }], headings: [], boxes: [], mainLast: last });
    });
    // 枠（備考など）
    if (boxes.length) {
      const lastPage = draw[draw.length - 1]!;
      const part = lastPage.parts[0]!;
      let pageRef = lastPage;
      let y = part.top + s.headH + part.rows.reduce((a, r) => a + r.h, 0) + reserveTable + boxGap;
      boxes.forEach((b, bi) => {
        if (bi >= main.boxesHere && (bi === main.boxesHere || y + b.h > bottom + 1e-6)) {
          pageRef = { first: false, parts: [], headings: [], boxes: [], mainLast: false };
          draw.push(pageRef);
          y = nextTop;
        }
        pageRef.boxes.push({ y, b });
        y += b.h + boxGap;
      });
    }
    // 枠のあとに付ける部品（dock end）：最後の枠の下（枠が無ければ表の下）。入らなければ次のページの上から
    if (endH > 0) {
      let page = draw[draw.length - 1]!;
      const lastBox = page.boxes[page.boxes.length - 1];
      const part = page.parts[0];
      let y = lastBox ? lastBox.y + lastBox.b.h : part ? part.top + s.headH + part.rows.reduce((a, r) => a + r.h, 0) + reserveTable : nextTop - 3;
      if (y + 3 + endH > bottom + 1e-6) {
        page = { first: false, parts: [], headings: [], boxes: [], mainLast: false };
        draw.push(page);
        y = nextTop - 3;
      }
      page.endY = y;
    }
    // 内訳の紙
    let cur: DrawPage | null = null;
    let curY = 0;
    sections.forEach((sec, si) => {
      const secRows = sec.rows.map((r, i) => rowOf(t, s, r, refLabel, i % 2 === 1));
      const total = t.foot ? footOf(t, s, { kind: "total", label: sec.totalLabel, sub: "", amount: sec.total }, true) : rowOf(t, s, { kind: "sheetTotal", no: "", name: sec.totalLabel, spec: "", qty: "", unit: "", price: "", amount: sec.total, notes: "" }, refLabel, false);
      const hH = headingH(t);
      const secNote = sec.taxNote ? 3 : 0;
      let newPage = si === 0 || data.sectionBreak === true || !cur;
      let y0 = curY + secSpec(t).gap;
      if (!newPage) {
        const lead = secRows.slice(0, 2).reduce((a, r) => a + r.h, 0) + (secRows.length <= 2 ? total.h : 0);
        if (y0 + hH + s.headH + lead + secNote > bottom + 1e-6) newPage = true;
      }
      if (newPage || !cur) {
        cur = { first: false, parts: [], headings: [], boxes: [], mainLast: false };
        draw.push(cur);
        // 2ページ目からも出す部品（番号・日付など）の下から。表紙の続きのページと同じ（#217 の不具合：repeat の印の無い前の書式で見出しが番号・日付に重なった）
        y0 = nextTop;
      }
      const anchor = `bk${si + 1}`;
      cur.headings.push({ y: y0, sec, anchor });
      secStart.set(sec.id, draw.length);
      const firstTop = y0 + hH;
      const pages = paginateRows(secRows, [total], { firstTop, nextTop, bottom, headH: s.headH, reserve: secNote, boxesH: [], fill: fillMain && data.sectionBreak === true, firstCap: undefined, blank, pitch: t.pitch });
      pages.pages.forEach((p, i) => {
        if (i > 0) { cur = { first: false, parts: [], headings: [], boxes: [], mainLast: false }; draw.push(cur); }
        const top = i === 0 ? firstTop : nextTop;
        const last = i === pages.pages.length - 1;
        cur!.parts.push({ top, rows: p.rows, header: true, ...(last && sec.taxNote ? { taxNote: sec.taxNote } : {}) });
        curY = top + s.headH + p.rows.reduce((a, r) => a + r.h, 0) + (last ? secNote : 0);
      });
    });
    return { draw };
  };

  // 1回目は「別紙 (P. 00)」で割り付けてページを決め、2回目に本当の番号で描く（番号の桁で行が変わることはまず無いが、変われば2回目の割り付けが正）
  // エディタは1ページ目だけを描くが、表紙の「別紙 (P. n)」は刷る紙と同じ番号にする（先に刷るときの割り付けで測る。#217 の本番の確かめ）
  if (sections.length && mode === "edit") {
    measuring = true;
    build();
    measuring = false;
  }
  let { draw } = build();
  if (sections.length && mode === "print") ({ draw } = build());
  const pageCount = draw.length + (opts.extraPages ?? 0);

  // 描く
  const geom: Geometry = { els: {}, table: { x: t.x, y: firstTableTop, w: t.w, h: 0 }, cols: s.cols.map((c) => ({ id: c.id, label: c.label, x: t.x + c.x, w: c.w })), dockBase: 0, boxes: [], overflow: [] };
  const rep = new Set(repeatEls(layout).map((e) => e.id));
  const pages = draw.map((d, pi) => {
    const items: Array<{ z: number; html: string }> = [];
    const pageNo = pi + 1;
    const mainPart = d.mainLast ? d.parts[0] : undefined;
    const tableBottom = mainPart ? mainPart.top + s.headH + mainPart.rows.reduce((a, r) => a + r.h, 0) : 0;
    for (const e of layout.els) {
      let b: Box;
      if (e.dock === "table") {
        if (!mainPart) continue;
        const q = tablePlan.place.get(e.id);
        const y = tableBottom + 3 + (q?.dy ?? e.dy ?? 0);
        const h = q?.h ?? e.h;
        b = e.type === "line" ? (e.dir === "v" ? { x: e.x, y, w: 0, h } : { x: e.x, y, w: e.w, h: 0 }) : { x: e.x, y, w: e.w, h };
      } else if (e.dock === "end") {
        if (d.endY == null || (mode === "print" && !endEls.includes(e))) continue;
        const q = endPlan.place.get(e.id);
        const y = d.endY + 3 + (q?.dy ?? e.dy ?? 0);
        const h = q?.h ?? e.h;
        b = e.type === "line" ? (e.dir === "v" ? { x: e.x, y, w: 0, h } : { x: e.x, y, w: e.w, h: 0 }) : { x: e.x, y, w: e.w, h };
      } else {
        if (!d.first && !rep.has(e.id)) continue;
        const p = placed.get(e.id);
        if (!p || (p.skip && d.first)) continue;
        b = d.first ? p.box : { x: e.x, y: topRep.has(e.id) ? r2(e.y - repShift) : e.y, w: e.w, h: e.h };
        if (e.type === "line") b = e.dir === "v" ? { ...b, w: 0 } : { ...b, h: 0 };
      }
      if (pi === 0) geom.els[e.id] = b;
      const z = e.type === "image" && e.guide ? 0 : e.type === "image" || e.type === "box" ? 1 : 3;
      items.push({ z, html: elHtml(e, b, data, pageNo, pageCount, mode, pre, assets, opts.labels) });
    }
    if (d.first && data.before) items.push({ z: 2, html: beforeHtml(t, t.y + shift, data.before, pre) });
    if (d.first && data.schedule) items.push({ z: 2, html: scheduleHtml(t, t.y + shift + beforeH(data), data.schedule, pre) });
    d.parts.forEach((part, k) => {
      const th = tableHtml(t, s, t.x, part, mode, pre, t.restText);
      items.push({ z: 2, html: th.html });
      if (pi === 0 && k === 0) {
        geom.table = { x: t.x, y: part.top, w: t.w, h: th.h };
        geom.dockBase = part.top + th.h + 3;
        geom.endBase = d.endY != null ? d.endY + 3 : geom.dockBase;
        for (const r of part.rows) for (const o of r.overflow ?? []) geom.overflow.push({ target: "table", text: o });
      }
    });
    for (const h of d.headings) items.push({ z: 2, html: headingHtml(t, t.x, h.y, h.sec, h.anchor, pre) });
    for (const bx of d.boxes) {
      items.push({ z: 2, html: boxHtml(t, t.x, bx.y, bx.b, pre) });
      if (pi === 0) geom.boxes.push({ x: t.x, y: bx.y, w: t.w, h: bx.b.h });
    }
    items.sort((a, b) => a.z - b.z);
    const wm = mode === "print" && data.watermark ? `<div class="wm">${esc(data.watermark)}</div>` : "";
    return items.map((x) => x.html).join("") + wm;
  });
  // 見本のデータで欄に入りきらない項目（エディタの注意）
  if (mode === "edit") geom.overflow.push(...fieldOverflows(layout, data));
  const plan: PlanPage[] = draw.map((d) => ({
    first: d.first,
    parts: d.parts.map((p) => ({
      top: p.top,
      bottom: p.top + (p.header ? s.headH : 0) + p.rows.reduce((a, r) => a + r.h, 0),
      rows: p.rows.map((r) => ({ kind: r.kind, h: r.h, text: r.cells.map((c) => c.lines.join(" ")).join("|"), rest: !!r.rest })),
    })),
    boxes: d.boxes.map((b) => ({ y: b.y, h: b.b.h, title: b.b.title })),
    headings: d.headings.map((h) => ({ y: h.y, title: h.sec.title })),
  }));
  return { pages, geom, pageCount, plan, bottom };
}

/** 項目の中身が枠に入りきらない（折った行の数 × 行の高さ ＞ 枠の高さ）。詰める組の項目は伸びるので数えない */
export function fieldOverflows(layout: PaperLayout, data: PaperData): Array<{ target: string; text: string }> {
  const out: Array<{ target: string; text: string }> = [];
  for (const e of layout.els) {
    if (e.type !== "field" || !e.on || e.fit || e.stack || (e.bind === "ack" && !e.head)) continue;
    const v = valueOf(e, data, 1, 1);
    if (!v.text || v.alt) continue;
    const fl = fieldLines(e, v);
    const need = fl.lines.length * lineMm(fl.size, e.lh ?? DEFAULT_LH);
    if (need > e.h + 0.3) out.push({ target: e.id, text: `${fl.lines.length}行` });
  }
  return out;
}

export function paperCss2(layout: PaperLayout, mode: RenderMode): string {
  const pg = layout.page;
  return `
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; }
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; color: #1A1A18; font-family: ${FONT_CSS.sans}; }
  .page { position: relative; width: ${mm(pg.w)}; height: ${mm(pg.h)}; overflow: hidden; background: #fff; }
  ${mode === "print" ? `@page { size: ${mm(pg.w)} ${mm(pg.h)}; margin: 0; } .page { page-break-after: always; break-after: page; } .page:last-child { page-break-after: auto; break-after: auto; }` : ""}
  .page .inner { position: absolute; left: 0; top: 0; width: 100%; height: 100%; ${mode === "print" && (pg.offset[0] || pg.offset[1]) ? `transform: translate(${mm(pg.offset[0])}, ${mm(pg.offset[1])});` : ""} }
  .wm { position: absolute; top: 40%; left: 0; right: 0; text-align: center; font-size: 72pt; font-weight: 700; color: rgba(160, 40, 40, 0.16); transform: rotate(-20deg); letter-spacing: 0.3em; pointer-events: none; z-index: 10; font-family: ${FONT_CSS.sans}; }
`;
}

export function renderPaperHtml2(layout: PaperLayout, data: PaperData, opts: Render2Options = {}): string {
  const mode = opts.mode ?? "print";
  const { pages } = renderPages2(layout, data, opts);
  return `<!doctype html><html lang="ja"><head><meta charset="utf-8"><title>${esc(layout.name)}</title><style>${paperCss2(layout, mode)}</style></head><body>${pages.map((p) => `<div class="page"><div class="inner">${p}</div></div>`).join("")}</body></html>`;
}
